import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, CircularProgress, MenuItem, Select, FormControl,
  InputLabel, Stack, Typography, Divider, Avatar, Alert, Card, Button
} from '@mui/material';
import { CheckCircle, WavingHand, TimerOutlined, History } from '@mui/icons-material';
import toast, { Toaster } from 'react-hot-toast';
import configService from '../services/config';
import { useSelector } from 'react-redux';

const STEPS = { IDLE: 'idle', CAMERA: 'camera', PREVIEW: 'preview', DONE: 'done' };

export default function PunchOut() {
  const [step, setStep] = useState(STEPS.IDLE);
  const [now, setNow] = useState(new Date());
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [photo, setPhoto] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationLabel, setLocationLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Attendance States
  const [statusData, setStatusData] = useState({ in: false, out: false, in_time: null, out_time: null });
  const [cooldownTime, setCooldownTime] = useState(null);
  const [stream, setStream] = useState(null);

  const { company_id, userId } = useSelector(state => state.globalVariables);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // 1. Live Clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // 2. Initial Data & Cooldown Logic
  const checkStatusAndLoadSites = useCallback(async () => {
    try {
      const [statusRes, sites] = await Promise.all([
        configService.getTodayStatus({ employee_id: userId, company_id: company_id }),
        configService.get_customer({ company_id: company_id })
      ]);

      if (statusRes.status) {
        setStatusData(statusRes.data);

        // Match with last_site_id for punch-out and auto-select it at the time of punchout
        const lockedSiteId = statusRes.data?.last_site_id || statusRes.data?.site_id;
        if (lockedSiteId) {
          setSelectedCustomer(lockedSiteId);
        }
      }
      setCustomers(sites || []);
    } catch (err) {
      toast.error("Data loading failed");
    } finally {
      setInitialLoading(false);
    }
  }, [userId, company_id]);

  useEffect(() => {
    checkStatusAndLoadSites();
  }, [checkStatusAndLoadSites]);

  // 3. 30-Min Cooldown Countdown
  useEffect(() => {
    if (statusData.in && statusData.in_time && !statusData.out) {
      const punchInDate = new Date(statusData.in_time);
      const unlockTime = new Date(punchInDate.getTime() + 30 * 60000);

      const timer = setInterval(() => {
        const diff = unlockTime - new Date();
        if (diff > 0) {
          const mins = Math.floor((diff / 1000 / 60) % 60);
          const secs = Math.floor((diff / 1000) % 60);
          setCooldownTime(`${mins}:${secs < 10 ? '0' : ''}${secs}`);
        } else {
          setCooldownTime(null);
          clearInterval(timer);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [statusData]);

  // Camera & Submission Logic (Keeping your existing logic but updated for MUI)
  const startCamera = async () => {
    if (cooldownTime) {
      toast.error(`Please wait for cooldown: ${cooldownTime}`);
      return;
    }
    try {
      setStep(STEPS.PREVIEW);
      fetchLocation();


    } catch (err) {
      toast.error('Camera access denied!');
      setStep(STEPS.IDLE);
    }
  };

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.scale(-1, 1);
    ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
    setPhoto(canvas.toDataURL('image/jpeg'));
    if (stream) stream.getTracks().forEach(t => t.stop());
    setStep(STEPS.PREVIEW);
    fetchLocation();
  };

  const fetchLocation = () => {
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      setLocation({ lat: latitude, lng: longitude });
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
      const data = await res.json();
      setLocationLabel(data.display_name);
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const blob = await fetch(photo).then(res => res.blob());
      const formData = new FormData();
      formData.append('site_id', selectedCustomer);
      formData.append('type', "OUT");
      formData.append('employee_id', userId);
      formData.append('image', blob, 'punch_out.jpg');
      formData.append('latitude', location?.lat || 0);
      formData.append('longitude', location?.lng || 0);
      formData.append('place', locationLabel);
      formData.append('company_id', company_id);

      const response = await configService.punch(formData);
      if (response.status) {
        toast.success("Punch Out Successful!");
        setStep(STEPS.DONE);
        checkStatusAndLoadSites(); // Refresh data
      } else if (response?.status === false) {
        toast.error(response?.message);
      }
    } catch (err) {
      toast.error("Submission failed!");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const stopStream = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const fmtTime = (dateStr) => dateStr ? new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : "--:--";

  if (initialLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', minHeight: '80vh', alignItems: 'center', bgcolor: '#f4f6f9' }}>
      <Toaster />
      <Card sx={{
        maxWidth: 450, width: '100%', bgcolor: '#ffffff', color: '#1e293b',
        p: 4, borderRadius: 6, boxShadow: '0 15px 35px rgba(0,0,0,0.08)',
        textAlign: 'center', border: '1px solid #e2e8f0'
      }}>

        {/* Step: DONE or Already Punched Out */}
        {(statusData.out || step === STEPS.DONE) ? (
          <Stack spacing={3} alignItems="center">
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
              <CircularProgress variant="determinate" value={100} size={180} thickness={2} sx={{ color: '#d32f2f' }} />
              <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b' }}>{fmtTime(statusData.out_time)}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.5)', fontWeight: 600 }}>PUNCHED OUT</Typography>
              </Box>
            </Box>
            <CheckCircle sx={{ fontSize: 60, color: '#4caf50' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#1e293b' }}>Duty Ended</Typography>
            <Alert icon={<WavingHand />} severity="info" sx={{ bgcolor: '#e0f2fe', color: '#0369a1', width: '100%' }}>
              Great work today! See you tomorrow.
            </Alert>
            <Button fullWidth variant="contained" disabled sx={{ py: 2, borderRadius: 3, bgcolor: '#f1f5f9', color: '#94a3b8' }}>Shift Completed</Button>
          </Stack>
        ) : (
          /* Step: IDLE / PUNCHING PROCESS */
          <Stack spacing={3}>
            {step === STEPS.IDLE && (
              <>
                <Box sx={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                  <CircularProgress variant="determinate" value={100} size={200} thickness={1.5} sx={{ color: cooldownTime ? '#ff9800' : '#d32f2f' }} />
                  <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'monospace', color: '#1e293b' }}>
                      {cooldownTime ? cooldownTime : now.toLocaleTimeString([], { hour12: false })}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.5)', letterSpacing: 2, fontWeight: 600 }}>
                      {cooldownTime ? "COOLDOWN" : "CURRENT TIME"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ textAlign: 'left', mt: 2 }}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    disabled={Boolean(statusData?.last_site_id || statusData?.site_id)}
                    sx={{
                      bgcolor: '#f8fafc',
                      borderRadius: 2,
                    }}
                  >
                    <InputLabel
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        "&.Mui-focused": { color: '#d32f2f' },
                        "&.Mui-disabled": {
                          color: 'rgba(0, 0, 0, 0.5)',
                          WebkitTextFillColor: 'rgba(0, 0, 0, 0.5)'
                        }
                      }}
                    >
                      Select Work Site
                    </InputLabel>
                    <Select
                      value={selectedCustomer}
                      label="Select Work Site"
                      onChange={(e) => setSelectedCustomer(e.target.value)}
                      sx={{
                        color: '#1e293b',
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: '#e2e8f0'
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: '#cbd5e1'
                        },
                        "&.Mui-disabled": {
                          color: '#1e293b',
                          WebkitTextFillColor: '#1e293b',
                          ".MuiOutlinedInput-notchedOutline": {
                            borderColor: '#e2e8f0'
                          },
                          ".MuiSelect-icon": {
                            color: 'rgba(0, 0, 0, 0.4)'
                          }
                        }
                      }}
                    >
                      {customers.map((c) => (
                        <MenuItem key={c.id} value={c.id} sx={{ color: "#1e293b" }}>
                          {c.fname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {statusData.in_time && (
                  <Typography variant="body2" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, fontWeight: 500 }}>
                    <History fontSize="small" /> Last Punch In: {fmtTime(statusData.in_time)}
                  </Typography>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!selectedCustomer || !!cooldownTime}
                  onClick={startCamera}
                  sx={{
                    py: 2, borderRadius: 4, bgcolor: '#f44336', fontWeight: 'bold',
                    boxShadow: '0 8px 16px rgba(244, 67, 54, 0.15)',
                    '&:hover': { bgcolor: '#d32f2f' },
                    '&:disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' }
                  }}
                >
                  {cooldownTime ? `Locked (${cooldownTime})` : "Start Punch Out"}
                </Button>
              </>
            )}

            {/* Camera View */}
            {step === STEPS.CAMERA && (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 500,
                  margin: "0 auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* Camera Preview */}
                <Box
                  sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    position: "relative",
                    width: "100%",
                    aspectRatio: "3/4",
                    bgcolor: "#000",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                    border: "2px solid #d32f2f"
                  }}
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transform: "scaleX(-1)",
                    }}
                  />

                  {/* Face Guide */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "70%",
                      height: "60%",
                      border: "2px dashed rgba(255,255,255,0.7)",
                      borderRadius: "50%",
                      pointerEvents: "none",
                    }}
                  />
                </Box>

                {/* Controls BELOW camera */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  {/* Capture Button */}
                  <Box
                    onClick={capturePhoto}
                    sx={{
                      width: 75,
                      height: 75,
                      borderRadius: "50%",
                      background: "#fff",
                      border: "6px solid #f44336",
                      cursor: "pointer",
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transition: "0.2s",
                      "&:active": {
                        transform: "scale(0.9)",
                      },
                    }}
                  />

                  <Button
                    variant="text"
                    sx={{ color: "#64748b", textTransform: "none", fontWeight: 500 }}
                    onClick={() => {
                      stopStream();
                      setStep(STEPS.IDLE);
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}

            {/* Preview View */}
            {step === STEPS.PREVIEW && (
              <Stack spacing={3}>
                <Alert severity="success" sx={{ bgcolor: '#edf7ed', color: '#1e4620', border: '1px solid #c8e6c9' }}>
                  {locationLabel || "Locating..."}
                </Alert>
                <Stack direction="row" spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    onClick={handleSubmit}
                    disabled={loading || !location}
                    sx={{ py: 1.5, borderRadius: 3, fontWeight: 'bold' }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Confirm Out"}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
      </Card>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Box>
  );
}